import {
  Authenticated,
  useApiUrl,
  useGo,
  useList,
  useOne,
  useTranslate,
} from "@refinedev/core";
import { Col, Flex, Row, Space, Spin, Typography } from "antd";
import { useQuota } from "hooks/useQuota";
import styled, { css, keyframes } from "styled-components";
import { Button, Card, Select } from "@libertify/ui";
import { FeatureFlags, QuotaFeatures } from "@/constants";
import {
  FlashProjectProperties,
  GenerateButton,
  OptionButtonHeader,
} from "components/ExperienceButtons";
import { LanguageSelect } from "components/LanguageSelect/";
import { useCallback, useEffect, useState } from "react";
import { CardBadge } from "./components/CardBadge";
import { useAntTheme } from "hooks/useAntTheme/";
import { useFeatureFlag } from "hooks/useFeature/";
import { useSession } from "hooks/useSession";
import { SavedSettingsResponse } from "../types";
import { ProjectType } from "./types";
import { useProjectCreation } from "./hooks/useProjectCreation";
import { AnalyticsEvents } from "types/analytics";
import { useProjectWizardStore } from "./useProjectWizardStore";
import { REVIEW_STEP } from "./components/ProjectCreateWizard";
import { QuotaAlert } from "components/QuotaAlert/";
import { useUploadFiles } from "#src/hooks";
import { ComplianceFooter } from "./components/ComplianceFooter";
import { Breadcrumbs } from "components/Breadcrumbs/";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";

export const ProjectSelectType = () => {
  const t = useTranslate();
  const go = useGo();
  const { theme, isMobile } = useAntTheme();
  const apiUrl = useApiUrl();
  const { isBlocked } = useQuota();
  const { createProject, trackEvent, paywallFlow } = useProjectCreation();
  const { resetDismissed, setGraceProjectId } = useSoftPaywall();
  const {
    setProject,
    setStep,
    setPlaybookBypass,
    uploadedFileNames,
    uploadedFiles,
  } = useProjectWizardStore();

  const {
    getS3LinksToUploadFiles,
    uploadFileToS3,
    setFileList,
    aggregatedDocumentsBase,
  } = useUploadFiles({
    assetRequestProperties: {
      document_type: "Unknown",
      aggregate_documents: true,
    },
    stagedAssets: uploadedFiles.map((f) => ({ uid: f.uid })),
  });

  // sync the uploaded files with the useUploadFiles hook's fileList to generate aggregated base document
  useEffect(() => {
    setFileList(uploadedFiles.map((f) => ({ ...f })));
  }, [uploadedFiles, setFileList]);

  const isProjectQuotaReached = isBlocked(QuotaFeatures.MediaProject);
  const [isCreating, setIsCreating] = useState(false);
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);
  const [chosenLanguage, setChosenLanguage] = useState<string | null>("auto");
  const [selectedSavedSettingId, setSelectedSavedSettingId] = useState<
    string | null
  >(null);

  const { currentOrganization } = useSession();
  const isDocHeyGeniusEnabled = useFeatureFlag(FeatureFlags.DocHeyGenius);

  const { data: savedSettingsData } = useList<SavedSettingsResponse>({
    resource: `media/${currentOrganization?.id}/saved-settings`,
    queryOptions: {
      enabled: Boolean(currentOrganization?.id),
    },
  });
  const savedSettings = savedSettingsData?.data || [];

  const { data: selectedSettingData } = useOne<SavedSettingsResponse>({
    resource: `media/${currentOrganization?.id}/saved-settings`,
    id: selectedSavedSettingId ?? "",
    queryOptions: {
      enabled: Boolean(currentOrganization?.id && selectedSavedSettingId),
    },
  });

  const titleStyle = {
    marginBottom: 8,
    fontSize: isMobile ? 20 : 36,
    fontWeight: 600,
  };
  const subtitleStyle = { fontSize: 16 };

  const handleBackToUpload = () => {
    go({ to: "/media/projects/create" });
  };

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      go({ to: "/media/projects/create" });
    }
  }, []);

  /**
   * Creates a new media project, uploads files to S3, and navigates to the appropriate page.
   *
   * @param isFlash - If true, navigates to the slides-streaming page for flash mode;
   *                  otherwise navigates to the setup page
   * @param isHeyGeniusDoc - If true, tags the project as a "hey_genius_doc" project
   */
  const handleCreateAndNavigate = useCallback(
    async (isFlash: boolean, isHeyGeniusDoc = false) => {
      // if (uploadedFileNames.length === 0 || isCreating) return;

      // If file objects are missing (e.g. page was refreshed), go back to upload
      if (uploadedFiles.length === 0) {
        go({ to: "/media/projects/create" });
        return;
      }

      let projectType: ProjectType = "new_video";
      if (isFlash) projectType = "flash";
      if (isHeyGeniusDoc) projectType = "hey_genius";

      trackEvent(AnalyticsEvents.ProjectCreateInit, {
        project_type: projectType,
        page: "project_creation_wizard",
        paywall_flow: paywallFlow,
      });

      setIsCreating(true);

      try {
        const hasBrandKit =
          !!selectedSettingData?.data?.media_settings?.brand_kit_id;
        const bypassWithPlaybook =
          !isFlash &&
          !isHeyGeniusDoc &&
          !!selectedSavedSettingId &&
          !!selectedSettingData?.data;
        const project = await createProject(
          uploadedFileNames,
          "new_video",
          isHeyGeniusDoc
            ? [{ tag_key: "hey_genius_doc", tag_value: "hey_genius_doc" }]
            : [],
          chosenLanguage,
          isFlash,
          selectedSavedSettingId,
          hasBrandKit,
          bypassWithPlaybook ? { generate_brandkit: false } : undefined
        );
        setProject(project);

        const customUrl = (fileName: string) =>
          `${apiUrl}/media/projects/${project.id}/documents/${fileName}`;
        // Clone files so they are mutable (Immer freezes Zustand state objects)
        const mutableFiles = uploadedFiles.map((f) => ({ ...f }));
        const s3Links = await getS3LinksToUploadFiles({
          fileListToUpload: mutableFiles,
          customUrl,
        });
        // Create every document entity (aggregate included) before any file hits S3.
        // Register the aggregated "all_documents.pdf" placeholder so the
        // backend can merge the uploaded docs into a single document.
        if (aggregatedDocumentsBase && uploadedFiles.length > 1) {
          await getS3LinksToUploadFiles({
            fileListToUpload: [aggregatedDocumentsBase],
            customUrl,
          });
        }

        // Upload files to S3 via pre-signed URLs
        await Promise.all(uploadFileToS3(mutableFiles, s3Links));

        resetDismissed();
        if (paywallFlow === "soft_3_exp") {
          setGraceProjectId(project.id);
        }

        if (bypassWithPlaybook) {
          setStep(REVIEW_STEP);
          setPlaybookBypass({
            id: selectedSavedSettingId,
            name: selectedSettingData.data.name,
          });
        }

        if (isFlash) {
          go({ to: `/media/projects/${project.id}/slides-streaming` });
        } else {
          go({ to: `/media/projects/${project.id}/setup` });
        }
      } catch (error) {
        console.error("Error creating project:", error);
      } finally {
        setIsCreating(false);
      }
    },
    [
      uploadedFileNames,
      uploadedFiles,
      createProject,
      go,
      apiUrl,
      chosenLanguage,
      setProject,
      setStep,
      setPlaybookBypass,
      setFileList,
      getS3LinksToUploadFiles,
      uploadFileToS3,
      selectedSavedSettingId,
      selectedSettingData,
      paywallFlow,
      resetDismissed,
      setGraceProjectId,
      trackEvent,
    ]
  );

  return (
    <Authenticated key="projectCreate">
      <Breadcrumbs />
      <Flex
        vertical
        align="center"
        justify="center"
        style={{ padding: "40px 20px 120px" }}
        gap={40}
      >
        <AnimatedContent>
          <>
            <Typography.Title level={1} style={titleStyle}>
              {t("projects.components.ProjectCreationPage.optionsTitle")}
            </Typography.Title>
            <Typography.Text type="secondary" style={subtitleStyle}>
              {t("projects.components.ProjectCreationPage.chooseMode")}
            </Typography.Text>
          </>
        </AnimatedContent>
        <AnimatedSection style={{ width: "100%", maxWidth: 800 }}>
          <Spin spinning={isCreating}>
            <AnimatedOptions>
              <Card style={{ marginBottom: 32, textAlign: "center" }}>
                <Flex align="center" justify="center" gap={8}>
                  <Typography.Text strong>
                    {uploadedFileNames.join(", ")}
                  </Typography.Text>
                  <Button
                    variant="primary"
                    onClick={handleBackToUpload}
                    size="s"
                  >
                    {t("buttons.change")}
                  </Button>
                </Flex>
              </Card>
              <Flex vertical gap={24}>
                <Row gutter={[24, 24]} justify="center">
                  <Col xs={24} sm={8}>
                    <OptionCard
                      $variant="flash"
                      hoverable
                      onClick={() => {
                        if (isProjectQuotaReached) {
                          setShowQuotaAlert(true);
                          return;
                        }
                        handleCreateAndNavigate(true).catch((error) => {
                          console.error(error);
                        });
                      }}
                    >
                      <CardBadge
                        text={t(
                          "projects.components.ProjectCreationPage.recommended"
                        )}
                        color={theme.colorWarning}
                      />
                      <CardContentFlexWrapper>
                        <Flex vertical align="center">
                          <OptionButtonHeader variant="flash" />
                          <Space direction="vertical" size={"small"}>
                            <Typography.Text type="secondary">
                              {t(
                                "projects.components.ProjectCreationPage.flashProjectDescription"
                              )}
                            </Typography.Text>
                            <FlashProjectProperties />
                          </Space>
                        </Flex>
                        <LanguageSelect
                          includeAuto
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: "100%", marginTop: 16 }}
                          value={chosenLanguage}
                          getPopupContainer={(trigger) =>
                            trigger.parentElement!
                          }
                          showIcon={true}
                          onChange={setChosenLanguage}
                        />
                        {savedSettings.length > 0 && (
                          <Select
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: "100%", marginTop: 16 }}
                            placeholder={t(
                              "projects.components.ProjectCreationPage.selectSavedSettings"
                            )}
                            options={savedSettings.map((setting) => ({
                              value: setting.id,
                              label: setting.name,
                            }))}
                            value={selectedSavedSettingId}
                            onChange={setSelectedSavedSettingId}
                            allowClear
                          />
                        )}
                        <GenerateButton
                          style={{
                            backgroundColor: theme.colorWarning,
                            color: theme.colorWhite,
                            border: "none",
                          }}
                        />
                      </CardContentFlexWrapper>
                    </OptionCard>
                  </Col>
                  <Col xs={24} sm={8}>
                    <OptionCard
                      $variant="advanced"
                      hoverable
                      onClick={() => {
                        if (isProjectQuotaReached) {
                          setShowQuotaAlert(true);
                          return;
                        }
                        handleCreateAndNavigate(false, false).catch((error) => {
                          console.error(error);
                        });
                      }}
                      data-testid="advanced-mode-option"
                    >
                      <CardBadge
                        text={t(
                          "projects.components.ProjectCreationPage.advancedUsers"
                        )}
                      />
                      <CardContentFlexWrapper>
                        <Flex vertical align="center">
                          <OptionButtonHeader variant="advanced" />
                        </Flex>
                        <Flex vertical gap={1}>
                          {savedSettings.length > 0 && (
                            <Select
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: "100%" }}
                              placeholder={t(
                                "projects.components.ProjectCreationPage.selectSavedSettings"
                              )}
                              options={savedSettings.map((setting) => ({
                                value: setting.id,
                                label: setting.name,
                              }))}
                              value={selectedSavedSettingId}
                              onChange={setSelectedSavedSettingId}
                              allowClear
                            />
                          )}
                          <GenerateButton />
                        </Flex>
                      </CardContentFlexWrapper>
                    </OptionCard>
                  </Col>
                  {isDocHeyGeniusEnabled && (
                    <Col xs={24} sm={8}>
                      <OptionCard
                        $variant="advanced"
                        hoverable
                        onClick={() => {
                          if (isProjectQuotaReached) {
                            setShowQuotaAlert(true);
                            return;
                          }
                          handleCreateAndNavigate(false, true).catch(
                            (error) => {
                              console.error(error);
                            }
                          );
                        }}
                        data-testid="heygenius-doc-option"
                      >
                        <CardContentFlexWrapper>
                          <Flex vertical align="center">
                            <OptionButtonHeader variant="heyGeniusDoc" />
                          </Flex>
                          <GenerateButton />
                        </CardContentFlexWrapper>
                      </OptionCard>
                    </Col>
                  )}
                </Row>
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  <Typography.Text type="secondary">
                    {t(
                      "projects.components.ProjectCreationPage.youCanChangeEverythingLater"
                    )}
                  </Typography.Text>
                </div>
                {showQuotaAlert && <QuotaAlert />}
              </Flex>
            </AnimatedOptions>
          </Spin>
        </AnimatedSection>
        <ComplianceFooter />
      </Flex>
    </Authenticated>
  );
};

const fadeIn = keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

const CardContentFlexWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const AnimatedContent = styled.div`
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;
`;

const AnimatedSection = styled.div`
  animation: ${fadeIn} 0.8s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;
`;

const AnimatedOptions = styled.div`
  animation: ${fadeIn} 0.4s ease-out;
`;

const OptionCard = styled(Card)<{ $variant: "flash" | "advanced" }>`
  text-align: center;
  height: 100%;
  border-width: 2px;
  transition: all 0.3s ease;

  .ant-card-body {
    padding: 40px 24px;
    height: 100%;
  }

  ${({ $variant, theme }) =>
    $variant === "flash" &&
    css`
      border-color: ${theme.colorWarning};
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    `}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);

    ${({ $variant, theme }) =>
      $variant === "flash"
        ? css`
            border-color: ${theme.colorWarning};
          `
        : css`
            border-color: ${theme.colorPrimary};
          `}
  }
`;
